import { useEffect, useState } from 'react'
import {
  getQualityRejected,
  QualityRejectedItem,
  QualityRejectedLevel,
} from '@/api/showcase'
import { translateRejectionReason } from '@/showcase/terms'
import { track, trackError } from '@/lib/analytics'

/**
 * A9 — блок «N постов не прошли автоматическую проверку».
 *
 * Подгружает первую страницу quality-rejected раз при маунте.
 * Если items.length === 0 — ничего не рендерим.
 * Если есть — карточка с total и CTA «посмотреть», по клику drawer со списком.
 */
export function QualityRejectedBlock({ channelUUID }: { channelUUID: string }) {
  const [list, setList] = useState<{ items: QualityRejectedItem[]; total: number } | null>(null)
  const [open, setOpen] = useState(false)

  useEffect(() => {
    let cancelled = false
    getQualityRejected(channelUUID, { limit: 50, offset: 0 })
      .then((res) => {
        if (!cancelled) setList({ items: res.items, total: res.total })
      })
      .catch((e) => {
        // Тихий fail — это не критичный блок, не валим UX
        trackError(e, 'quality-rejected-list')
        if (!cancelled) setList({ items: [], total: 0 })
      })
    return () => { cancelled = true }
  }, [channelUUID])

  if (!list || list.total === 0) return null

  function handleOpen() {
    track('showcase_quality_rejected_open', {
      total: list?.total,
      message: `открыл список не прошедших проверку (${list?.total})`,
    })
    setOpen(true)
  }

  return (
    <>
      <div className="qr-block" role="region" aria-label="Не прошли проверку">
        <div className="qr-icon">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="9" />
            <path d="M12 8v4M12 16h.01" />
          </svg>
        </div>
        <div className="qr-text">
          <div className="qr-title">
            <b>{list.total}</b> {pluralPosts(list.total)} не&nbsp;прошли автоматическую проверку
          </div>
          <div className="qr-sub">
            Эти посты не попали в&nbsp;поиск — короткие, без текста, или AI отметил низкое качество.
          </div>
        </div>
        <button className="qr-cta" onClick={handleOpen}>
          Посмотреть
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M9 6l6 6-6 6" />
          </svg>
        </button>

        <style>{`
          .qr-block {
            display: flex;
            align-items: center;
            gap: 14px;
            padding: 14px 18px;
            margin-bottom: 16px;
            background: #fff8e1;
            border: 1px solid #f6c247;
            border-radius: 12px;
          }
          .qr-icon {
            width: 36px;
            height: 36px;
            border-radius: 10px;
            background: white;
            color: #8b6914;
            display: flex;
            align-items: center;
            justify-content: center;
            flex-shrink: 0;
          }
          .qr-icon svg { width: 18px; height: 18px; }
          .qr-text { flex: 1; min-width: 0; }
          .qr-title {
            font-size: 14px;
            color: var(--tg-ink, #111);
            line-height: 1.4;
            margin-bottom: 2px;
          }
          .qr-title b {
            font-weight: 800;
            font-variant-numeric: tabular-nums;
          }
          .qr-sub {
            font-size: 12.5px;
            color: var(--tg-ink-2, #333);
            line-height: 1.45;
          }
          .qr-cta {
            display: inline-flex;
            align-items: center;
            gap: 4px;
            padding: 8px 14px;
            background: white;
            color: #8b6914;
            border: 1px solid #f6c247;
            border-radius: 8px;
            font-family: inherit;
            font-size: 13px;
            font-weight: 700;
            cursor: pointer;
            flex-shrink: 0;
          }
          .qr-cta:hover { background: #fff4cc; }
          .qr-cta svg { width: 12px; height: 12px; }
          @media (max-width: 600px) {
            .qr-block { flex-wrap: wrap; }
            .qr-cta { width: 100%; justify-content: center; }
          }
        `}</style>
      </div>

      {open && list && (
        <QualityRejectedDrawer
          items={list.items}
          total={list.total}
          onClose={() => setOpen(false)}
        />
      )}
    </>
  )
}

function QualityRejectedDrawer({
  items, total, onClose,
}: {
  items: QualityRejectedItem[]
  total: number
  onClose: () => void
}) {
  return (
    <div className="qr-veil" onClick={onClose}>
      <div className="qr-drawer" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
        <header className="qr-d-header">
          <div>
            <h3 className="qr-d-title">Не прошли проверку</h3>
            <p className="qr-d-sub">Всего: <b>{total}</b></p>
          </div>
          <button className="qr-d-close" onClick={onClose} aria-label="Закрыть">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
        </header>

        <div className="qr-d-list">
          {items.length === 0 ? (
            <div className="qr-d-empty">Список пуст</div>
          ) : (
            items.map((item) => (
              <RejectedRow key={item.id} item={item} />
            ))
          )}
        </div>

        {items.length < total && (
          <div className="qr-d-more">
            Показано {items.length} из {total}. Остальные — на странице «Посты» (фильтр «не прошли проверку») когда она будет готова.
          </div>
        )}

        <footer className="qr-d-foot">
          <div className="qr-d-explain">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="9" />
              <path d="M12 16v-4M12 8h.01" />
            </svg>
            <span>
              <b>L1</b> — отсеяно ещё до AI (длина, стоп-слова, эмодзи). <b>L2</b> — AI сгенерил, но не прошёл фильтры (SEO-стоп-слова, длина title). <b>L3</b> — низкий quality score.
            </span>
          </div>
        </footer>

        <style>{`
          .qr-veil {
            position: fixed;
            inset: 0;
            background: rgba(38, 40, 45, 0.55);
            backdrop-filter: blur(4px);
            display: flex;
            align-items: stretch;
            justify-content: flex-end;
            z-index: 1000;
          }
          .qr-drawer {
            background: white;
            width: 100%;
            max-width: 520px;
            display: flex;
            flex-direction: column;
            box-shadow: -24px 0 56px -16px rgba(0,0,0,0.3);
            animation: qr-slide 200ms ease-out;
          }
          @keyframes qr-slide {
            from { transform: translateX(100%); }
            to { transform: translateX(0); }
          }
          .qr-d-header {
            display: flex;
            justify-content: space-between;
            align-items: flex-start;
            padding: 20px 22px;
            border-bottom: 1px solid var(--tg-line-2, #f0f0f4);
          }
          .qr-d-title { margin: 0 0 2px; font-size: 18px; font-weight: 800; color: var(--tg-ink, #111); }
          .qr-d-sub { margin: 0; font-size: 13px; color: var(--tg-mute, #5b6678); }
          .qr-d-sub b { color: var(--tg-ink, #111); font-variant-numeric: tabular-nums; }
          .qr-d-close {
            background: none;
            border: 0;
            color: var(--tg-mute, #5b6678);
            padding: 4px;
            cursor: pointer;
            border-radius: 6px;
          }
          .qr-d-close:hover { background: var(--tg-paper, #fafafd); color: var(--tg-ink, #111); }
          .qr-d-close svg { width: 18px; height: 18px; display: block; }
          .qr-d-list {
            flex: 1;
            overflow-y: auto;
            padding: 12px 22px;
            display: flex;
            flex-direction: column;
            gap: 10px;
          }
          .qr-d-empty {
            text-align: center;
            font-size: 13px;
            color: var(--tg-mute, #5b6678);
            padding: 32px 0;
          }
          .qr-d-more {
            padding: 12px 22px;
            font-size: 12px;
            color: var(--tg-mute, #5b6678);
            border-top: 1px solid var(--tg-line-2, #f0f0f4);
          }
          .qr-d-foot {
            padding: 14px 22px;
            border-top: 1px solid var(--tg-line-2, #f0f0f4);
            background: var(--tg-paper, #fafafd);
          }
          .qr-d-explain {
            display: flex;
            align-items: flex-start;
            gap: 8px;
            font-size: 11.5px;
            color: var(--tg-mute, #5b6678);
            line-height: 1.5;
          }
          .qr-d-explain svg { width: 14px; height: 14px; flex-shrink: 0; margin-top: 1px; }
          .qr-d-explain b { color: var(--tg-ink, #111); font-weight: 700; }
        `}</style>
      </div>
    </div>
  )
}

function RejectedRow({ item }: { item: QualityRejectedItem }) {
  return (
    <article className="rr">
      <div className="rr-head">
        <span className={`rr-level rr-level-${item.level.toLowerCase()}`}>{item.level}</span>
        <span className="rr-date">{formatRuDate(new Date(item.createdAt))}</span>
      </div>
      <div className="rr-reason">{translateRejectionReason(item.reason)}</div>
      <div className="rr-meta">
        <code className="rr-code">{item.reason}</code>
        <span className="rr-sep">·</span>
        sourcePostId&nbsp;<code>{item.sourcePostId}</code>
      </div>

      <style>{`
        .rr {
          padding: 12px 14px;
          background: var(--tg-paper, #fafafd);
          border-radius: 10px;
          display: flex;
          flex-direction: column;
          gap: 4px;
        }
        .rr-head {
          display: flex;
          justify-content: space-between;
          align-items: center;
          font-size: 11.5px;
        }
        .rr-level {
          font-weight: 800;
          letter-spacing: 0.06em;
          padding: 2px 8px;
          border-radius: 4px;
          font-size: 10.5px;
        }
        .rr-level-l1 { background: rgba(229, 70, 70, 0.12); color: #c53030; }
        .rr-level-l2 { background: rgba(255, 145, 0, 0.12); color: #c2620e; }
        .rr-level-l3 { background: rgba(0, 102, 255, 0.12); color: #0052cc; }
        .rr-date { color: var(--tg-mute, #5b6678); }
        .rr-reason {
          font-size: 13.5px;
          color: var(--tg-ink, #111);
          line-height: 1.45;
          font-weight: 600;
        }
        .rr-meta {
          font-size: 11.5px;
          color: var(--tg-mute, #5b6678);
          display: flex;
          align-items: center;
          gap: 6px;
          flex-wrap: wrap;
        }
        .rr-meta code {
          font-family: ui-monospace, SFMono-Regular, monospace;
          background: white;
          padding: 1px 6px;
          border-radius: 4px;
          font-size: 11px;
        }
        .rr-sep { color: var(--tg-line-3, #d0d0d8); }
      `}</style>
    </article>
  )
}

function pluralPosts(n: number): string {
  const mod10 = n % 10
  const mod100 = n % 100
  if (mod10 === 1 && mod100 !== 11) return 'пост'
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return 'поста'
  return 'постов'
}

const MONTHS_RU = ['янв', 'фев', 'мар', 'апр', 'мая', 'июн', 'июл', 'авг', 'сен', 'окт', 'ноя', 'дек']
function formatRuDate(d: Date): string {
  return `${d.getDate()} ${MONTHS_RU[d.getMonth()]}`
}
